import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtUSD, fmtDate, relDays, CHART_COLORS } from "../lib/format";
import { Overline, StatCard, SectionCard, Monogram, ScorePill, RiskBadge, EmptyState } from "../components/shared";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, CartesianGrid,
} from "recharts";
import {
  Boxes, Sparkles, Flame, Landmark, TrendingUp, AlertTriangle, CalendarClock, Rocket, Loader2, Wallet,
} from "lucide-react";

function ChartCard({ title, children, testId }) {
  return (
    <SectionCard title={title} testId={testId} className="h-full">
      <div className="h-56">{children}</div>
    </SectionCard>
  );
}

const tooltipStyle = { fontSize: 12, borderRadius: 4, border: "1px solid hsl(214 24% 90%)", fontFamily: "IBM Plex Sans" };

function ProjectMiniRow({ p }) {
  return (
    <Link to={`/project/${p.id}`} className="flex items-center gap-3 py-2.5 px-1 border-b border-border/40 last:border-0 hover:bg-muted/50 transition-colors group" data-testid={`mini-row-${p.id}`}>
      <Monogram symbol={p.token_symbol} name={p.name} size={30} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{p.name}</div>
        <div className="text-[11px] text-muted-foreground truncate">{p.category} · {p.stage}</div>
      </div>
      <ScorePill score={p.score} size="sm" />
    </Link>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  const k = data.kpis;

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <Overline>Market Intelligence</Overline>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Global Token-Launch Dashboard</h1>
        </div>
        <div className="text-xs text-muted-foreground">Daily scan · {data.upcoming_tge.length} TGEs in the next 60 days</div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Tracked Projects" value={k.total_projects} sub={`${k.launched} launched`} icon={Boxes} testId="kpi-total" />
        <StatCard label="New Today" value={k.new_today} sub="Newly discovered" icon={Sparkles} tone="text-blue-700" testId="kpi-new" />
        <StatCard label="Active Sales" value={k.active_sales} sub={`${k.upcoming_launches} launching ≤90d`} icon={Flame} tone="text-amber-700" testId="kpi-active" />
        <StatCard label="Total Raised" value={fmtUSD(k.total_funding)} sub="Across tracked market" icon={Wallet} tone="text-emerald-700" testId="kpi-funding" />
        <StatCard label="Avg. Intelligence Score" value={`${k.avg_score}/100`} sub="Market average" icon={TrendingUp} testId="kpi-avgscore" />
        <StatCard label="High-Risk Projects" value={k.high_risk} sub="High / Critical flags" icon={AlertTriangle} tone="text-red-700" testId="kpi-risk" />
        <StatCard label="Upcoming TGEs" value={data.upcoming_tge.length} sub="Next 60 days" icon={CalendarClock} tone="text-indigo-700" testId="kpi-tge" />
        <StatCard label="Recently Launched" value={k.launched} sub="Post-launch tracking" icon={Rocket} tone="text-violet-700" testId="kpi-launched" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Projects by Sector" testId="chart-sector">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.charts.by_sector.slice(0, 10)} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid horizontal={false} stroke="hsl(214 24% 93%)" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={104} tick={{ fontSize: 10 }} interval={0} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                {data.charts.by_sector.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Projects by Chain" testId="chart-chain">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.charts.by_chain.slice(0, 10)} margin={{ left: -14, right: 8 }}>
              <CartesianGrid vertical={false} stroke="hsl(214 24% 93%)" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {data.charts.by_chain.map((_, i) => <Cell key={i} fill={CHART_COLORS[(i + 3) % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Decentralization Distribution" testId="chart-decentralization">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.charts.by_decentralization} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                {data.charts.by_decentralization.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-3 gap-y-1 -mt-4 justify-center">
            {data.charts.by_decentralization.map((d, i) => (
              <span key={d.name} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{d.name}
              </span>
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Funding by Sector (USD)" testId="chart-funding">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.charts.funding_by_sector.slice(0, 8)} margin={{ left: 4, right: 8 }}>
              <CartesianGrid vertical={false} stroke="hsl(214 24% 93%)" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtUSD(v)} width={54} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtUSD(v)} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]} fill="#059669" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Projects by Launch Stage" testId="chart-stage">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.charts.by_stage} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={38} outerRadius={70} paddingAngle={2}>
                {data.charts.by_stage.map((_, i) => <Cell key={i} fill={CHART_COLORS[(i + 2) % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-3 gap-y-1 -mt-4 justify-center">
            {data.charts.by_stage.map((d, i) => (
              <span key={d.name} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[(i + 2) % CHART_COLORS.length] }} />{d.name} ({d.value})
              </span>
            ))}
          </div>
        </ChartCard>

        <SectionCard title="Upcoming TGEs" testId="list-tge" className="h-full">
          <div className="max-h-56 overflow-y-auto no-scrollbar">
            {data.upcoming_tge.length ? data.upcoming_tge.map((p) => (
              <Link key={p.id} to={`/project/${p.id}`} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0 hover:bg-muted/50 transition-colors" data-testid={`tge-${p.id}`}>
                <Monogram symbol={p.token_symbol} name={p.name} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(p.tge_date)} · {relDays(p.tge_date)}</div>
                </div>
                <ScorePill score={p.score} size="sm" />
              </Link>
            )) : <EmptyState title="No upcoming TGEs" icon={CalendarClock} />}
          </div>
        </SectionCard>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SectionCard title="Highest Rated" testId="list-rated">{data.highest_rated.map((p) => <ProjectMiniRow key={p.id} p={p} />)}</SectionCard>
        <SectionCard title="Highest Risk" testId="list-risk">
          {data.highest_risk.map((p) => (
            <Link key={p.id} to={`/project/${p.id}`} className="flex items-center gap-3 py-2.5 border-b border-border/40 last:border-0 hover:bg-muted/50 transition-colors" data-testid={`risk-${p.id}`}>
              <Monogram symbol={p.token_symbol} name={p.name} size={30} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{p.red_flag_count} flags</div>
              </div>
              <RiskBadge level={p.risk_level} />
            </Link>
          ))}
        </SectionCard>
        <SectionCard title="Most Funded" testId="list-funded">
          {data.most_funded.map((p) => (
            <Link key={p.id} to={`/project/${p.id}`} className="flex items-center gap-3 py-2.5 border-b border-border/40 last:border-0 hover:bg-muted/50 transition-colors" data-testid={`funded-${p.id}`}>
              <Monogram symbol={p.token_symbol} name={p.name} size={30} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{p.category}</div>
              </div>
              <span className="num text-xs font-bold text-emerald-700">{fmtUSD(p.funds_raised)}</span>
            </Link>
          ))}
        </SectionCard>
        <SectionCard title="New Today" testId="list-new">
          {data.new_today.length ? data.new_today.map((p) => <ProjectMiniRow key={p.id} p={p} />) : <EmptyState title="Nothing new today" sub="Run a research scan to discover more." icon={Sparkles} />}
        </SectionCard>
      </div>
    </div>
  );
}
